/**
 * Title Portal API Service
 * 
 * Handles all API communication with the title portal endpoints.
 */

import axios from 'axios'
import store from '@/store'
import router from '@/router'

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Request interceptor - add session token to authenticated requests
apiClient.interceptors.request.use(
  (config) => {
    const sessionToken = store.getters.sessionToken
    
    if (sessionToken) {
      config.headers.Authorization = `TitlePortal ${sessionToken}`
    }
    
    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`)
    
    return config
  },
  (error) => {
    console.error('[API] Request error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => {
    console.log(`[API] Response ${response.status} from ${response.config.url}`)
    return response
  },
  (error) => {
    console.error('[API] Response error:', error.response?.status, error.message)
    
    // Handle 401/403 Unauthorized/Forbidden - session expired or invalid
    if (error.response?.status === 401 || error.response?.status === 403) {
      router.push({ name: 'LinkExpired' })
    }
    
    return Promise.reject(error)
  }
)

/**
 * Validate a magic link token and create a session.
 * 
 * @param {string} token - The magic link token UUID
 * @returns {Promise<Object>} Session data including session_token, settlement_agent, loan, and closing_request
 */
async function validateToken(token) {
  console.log(`[API] Validating token: ${token}`)
  
  const response = await apiClient.get(`/title-portal/validate/${token}/`)
  
  console.log('[API] Token validated successfully')
  
  return response.data
}

/**
 * Get the current closing request status.
 * 
 * @returns {Promise<Object>} Closing request status data
 */
async function getClosingStatus() {
  console.log('[API] Fetching closing status')
  
  const response = await apiClient.get('/title-portal/closing-status/')
  
  return response.data
}

/**
 * Update closing request status (for testing/admin or manual updates).
 * 
 * @param {Object} statusData - Status data to update
 * @param {string} statusData.closing_date - Optional closing date (YYYY-MM-DD)
 * @param {boolean} statusData.title_info_complete - Optional completion flag
 * @param {boolean} statusData.fees_complete - Optional completion flag
 * @param {boolean} statusData.payoffs_complete - Optional completion flag
 * @param {boolean} statusData.preview_generated - Optional completion flag
 * @param {boolean} statusData.documents_generated - Optional completion flag
 * @returns {Promise<Object>} Updated closing status
 */
async function updateClosingStatus(statusData) {
  console.log('[API] Updating closing status:', statusData)
  
  const response = await apiClient.patch('/title-portal/closing-status/', statusData)
  
  return response.data
}

/**
 * Get title information (vesting, legal description).
 * 
 * @returns {Promise<Object>} Title info data
 */
async function getTitleInfo() {
  console.log('[API] Fetching title info')
  
  const response = await apiClient.get('/title-portal/title-info/')
  
  return response.data
}

/**
 * Update title information.
 * 
 * @param {Object} titleData - Title info to update
 * @returns {Promise<Object>} Updated title info
 */
async function updateTitleInfo(titleData) {
  console.log('[API] Updating title info')
  
  const response = await apiClient.patch('/title-portal/title-info/', titleData)
  
  return response.data
}

/**
 * Get fees for the loan.
 * 
 * @returns {Promise<Object>} Fees data with list and tolerance status
 */
async function getFees() {
  console.log('[API] Fetching fees')
  
  const response = await apiClient.get('/title-portal/fees/')
  
  return response.data
}

/**
 * Create a new fee.
 * 
 * @param {Object} feeData - Fee data to create
 * @returns {Promise<Object>} Created fee with tolerance check result
 */
async function createFee(feeData) {
  console.log('[API] Creating fee')
  
  const response = await apiClient.post('/title-portal/fees/', feeData)
  
  return response.data
}

/**
 * Update an existing fee.
 * 
 * @param {number} feeId - Fee ID to update
 * @param {Object} feeData - Fee data to update
 * @returns {Promise<Object>} Updated fee with tolerance check result
 */
async function updateFee(feeId, feeData) {
  console.log(`[API] Updating fee ${feeId}`)
  
  const response = await apiClient.patch(`/title-portal/fees/${feeId}/`, feeData)
  
  return response.data
}

/**
 * Delete a fee.
 * 
 * @param {number} feeId - Fee ID to delete
 * @returns {Promise<void>}
 */
async function deleteFee(feeId) {
  console.log(`[API] Deleting fee ${feeId}`)
  
  await apiClient.delete(`/title-portal/fees/${feeId}/`)
}

/**
 * Get tolerance status for the loan.
 * 
 * @returns {Promise<Object>} Tolerance status data
 */
async function getToleranceStatus() {
  console.log('[API] Fetching tolerance status')
  
  const response = await apiClient.get('/title-portal/tolerance-status/')
  
  return response.data
}

/**
 * Get payoffs for the loan.
 * 
 * @returns {Promise<Object>} Payoffs data
 */
async function getPayoffs() {
  console.log('[API] Fetching payoffs')
  
  const response = await apiClient.get('/title-portal/payoffs/')
  
  return response.data
}

/**
 * Create a new payoff.
 * 
 * @param {Object} payoffData - Payoff data to create
 * @returns {Promise<Object>} Created payoff
 */
async function createPayoff(payoffData) {
  console.log('[API] Creating payoff')
  
  const response = await apiClient.post('/title-portal/payoffs/', payoffData)
  
  return response.data
}

/**
 * Update a payoff.
 * 
 * @param {number} payoffId - Payoff ID to update
 * @param {Object} payoffData - Payoff data to update
 * @returns {Promise<Object>} Updated payoff
 */
async function updatePayoff(payoffId, payoffData) {
  console.log(`[API] Updating payoff ${payoffId}`)
  
  const response = await apiClient.patch(`/title-portal/payoffs/${payoffId}/`, payoffData)
  
  return response.data
}

/**
 * Upload HUD-1 document.
 * 
 * @param {File} file - The HUD-1 PDF file
 * @returns {Promise<Object>} Upload result
 */
async function uploadHud(file) {
  console.log('[API] Uploading HUD-1')
  
  const formData = new FormData()
  formData.append('file', file)
  
  const response = await apiClient.post('/title-portal/hud-upload/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  
  return response.data
}

/**
 * Generate HUD-1 Settlement Statement preview.
 * 
 * @returns {Promise<Object>} Generation result with preview URL
 */
async function generateHudPreview() {
  console.log('[API] Generating HUD-1 preview')
  
  const response = await apiClient.post('/title-portal/preview/hud/')
  
  return response.data
}

/**
 * Generate Security Instruments preview.
 * 
 * @returns {Promise<Object>} Generation result with preview URL
 */
async function generateSecurityPreview() {
  console.log('[API] Generating security instruments preview')
  
  const response = await apiClient.post('/title-portal/preview/security/')
  
  return response.data
}

/**
 * Generate Closing Instructions preview.
 * 
 * @returns {Promise<Object>} Generation result with preview URL
 */
async function generateInstructionsPreview() {
  console.log('[API] Generating closing instructions preview')
  
  const response = await apiClient.post('/title-portal/preview/instructions/')
  
  return response.data
}

/**
 * Get list of saved trustees.
 * 
 * @returns {Promise<Object>} List of trustees
 */
async function getTrustees() {
  console.log('[API] Fetching trustees')
  
  const response = await apiClient.get('/title-portal/trustees/')
  
  return response.data
}

/**
 * Save trustee selection for the loan.
 * 
 * @param {Object} trusteeData - Trustee data (existing ID or new trustee info)
 * @returns {Promise<Object>} Updated trustee info
 */
async function saveTrustee(trusteeData) {
  console.log('[API] Saving trustee')
  
  const response = await apiClient.post('/title-portal/trustee/', trusteeData)
  
  return response.data
}

/**
 * Get list of saved settlement agents.
 * 
 * @returns {Promise<Object>} List of settlement agents
 */
async function getSettlementAgents() {
  console.log('[API] Fetching settlement agents')
  
  const response = await apiClient.get('/title-portal/settlement-agents/')
  
  return response.data
}

/**
 * Save settlement agent selection for the loan.
 * 
 * @param {Object} agentData - Settlement agent data (existing ID or new agent info)
 * @returns {Promise<Object>} Updated settlement agent info
 */
async function saveSettlementAgent(agentData) {
  console.log('[API] Saving settlement agent')
  
  const response = await apiClient.post('/title-portal/settlement-agent/', agentData)
  
  return response.data
}

/**
 * Generate closing documents.
 * 
 * @param {Object} options - Document generation options
 * @param {string[]} options.document_types - Array of document types to generate (e.g., ['hud', 'security_instruments', 'closing_instructions'])
 * @returns {Promise<Object>} Generation result with package_id and documents array
 */
async function generateDocuments(options = {}) {
  console.log('[API] Generating closing documents with options:', options)
  
  const payload = {
    document_types: options.document_types || ['hud', 'security_instruments'],
  }
  
  const response = await apiClient.post('/title-portal/generate-documents/', payload)
  
  return response.data
}

/**
 * Get loan officer contact information.
 * 
 * @returns {Promise<Object>} Loan officer contact data
 */
async function getLoanOfficer() {
  console.log('[API] Fetching loan officer info')
  
  const response = await apiClient.get('/title-portal/loan-officer/')
  
  return response.data
}

export default {
  validateToken,
  getClosingStatus,
  updateClosingStatus,
  getTitleInfo,
  updateTitleInfo,
  getFees,
  createFee,
  updateFee,
  deleteFee,
  getToleranceStatus,
  getPayoffs,
  createPayoff,
  updatePayoff,
  uploadHud,
  generateHudPreview,
  generateSecurityPreview,
  generateInstructionsPreview,
  generateDocuments,
  getLoanOfficer,
  getTrustees,
  saveTrustee,
  getSettlementAgents,
  saveSettlementAgent,
}

